import { memo, useState } from "react";
import { useTranslation } from 'react-i18next';
import styled from "styled-components";
import { getDefaultStore, useAtom } from 'jotai';
import { uploadMapDataPanelOpenAtom } from "./store/UploadMapDataPanelStore";
import { Button, Input, Modal, Select, Switch } from "antd";
import Dragger from "antd/es/upload/Dragger";
import { InboxOutlined } from '@ant-design/icons';
import { MapData_Create_Request, MapData_ProcessStatus } from "~/common/typescript/models/MapData";
import { getAxiosInstance } from "~/common/react/utils/networkUtil";
import { MapProjection, mapProjectionList } from "~/common/typescript/services/map_projection/MapProjection";
import { remote_mapData_getByProject, remote_mapData_update } from "~/common/typescript/apis/MapDataAPIs";
import { currentProjectAtom } from "~/stores/ProjectStore";
import { mapDataListAtom } from "../../stores/mapStore";

export const showUploadMapDataPanel = () => {
  getDefaultStore().set(uploadMapDataPanelOpenAtom, true);
};

export const hideUploadMapDataPanel = () => {
  getDefaultStore().set(uploadMapDataPanelOpenAtom, false);
};

const UploadMapDataPanel = () => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useAtom(uploadMapDataPanelOpenAtom);

  const [mapDataLayers, setMapDataLayers] = useAtom(mapDataListAtom);

  const [currentProject, setCurrentProject] = useAtom(currentProjectAtom);

  const [name, setName] = useState<string>('');
  const [projection, setProjection] = useState<MapProjection>();
  const [encoding, setEncoding] = useState<string>('');

  const [uploadResultData, setUploadResultData] = useState<any>();

  const [gdalInfo, setGdalInfo] = useState<any>();
  const [ogrInfo, setOgrInfo] = useState<any>();

  const [componentKey, setComponentKey] = useState<number>(0);

  const handleUpload = async (options: any) => {
    const { onSuccess, onError, file, onProgress } = options;
    setName(file.name);

    if (!currentProject) {
      console.error('currentProject is null');
      return;
    }

    const createRequest: MapData_Create_Request = {
      name: file.name,
      description: 'Dữ liệu',
      processInputFiles: [file],
      processInputMetadata: '{}',
      projectId: currentProject.id
    }

    const formData = new FormData();
    formData.append('file', file);

    try {
      const formData = new FormData();
      {
        formData.append('name', createRequest.name);
        formData.append('description', createRequest.description);
        for (let i = 0; i < createRequest.processInputFiles.length; i++) {
          formData.append('processInputFiles', createRequest.processInputFiles[i]);
        }

        if (createRequest.publish_status) {
          formData.append('publish_status', createRequest.publish_status);
        }
        if (createRequest.metadata) {
          formData.append('metadata', createRequest.metadata);
        }
        if (createRequest.processInputMetadata) {
          formData.append('processInputMetadata', createRequest.processInputMetadata);
        }
        if (createRequest.projectId) {
          formData.append('projectId', createRequest.projectId.toString());
        }
      }
      getAxiosInstance()
        .post('/api/map-data-ext', formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
          onUploadProgress: (progressEvent) => {
            const percentCompleted = Math.round((progressEvent.loaded * 100) / (progressEvent.total || 100));
            onProgress({ percent: percentCompleted });
          }
        })
        .then((response) => {
          console.log('SUCCESS: when remote_mapData_create');
          onSuccess(response.data);
          console.log('response.data:', response.data);

          const metadata = JSON.parse(response.data.data?.metadata || '{}');
          if (metadata.gdalinfo) {
            setGdalInfo(JSON.parse(metadata.gdalinfo));
          } else if (metadata.ogrinfo) {
            setOgrInfo(JSON.parse(metadata.ogrinfo));
          }
          setUploadResultData(response.data);
        })
        .catch((e) => {
          console.log('ERROR: when remote_mapData_create', e);
          onError(e);
        });
    } catch (error) {
      onError(error);
      console.error('Upload error:', error);
    }
  }

  const reloadMapDataList = () => {
    if (!currentProject || !currentProject.id) {
      return;
    }

    if (!localStorage.jwt) {
      return;
    }

    remote_mapData_getByProject(currentProject.id).then((res) => {
      const temporaryMapDataLayers = JSON.parse(JSON.stringify(res));

      // Chỉ thêm mới layer, không được thay thế layer cũ, tránh mất mát dữ liệu
      for (let i = 0; i < temporaryMapDataLayers.length; i++) {
        try {
          const existingLayer = mapDataLayers.find(layer => layer?.id === temporaryMapDataLayers[i]?.id);
          if (existingLayer) {
            temporaryMapDataLayers[i] = {
              ...temporaryMapDataLayers[i],
              visible: existingLayer.visible ?? false,
            };
          }
        } catch (error) {
          console.error('error', error);
        }
      }
      setMapDataLayers(temporaryMapDataLayers);
    }).catch((error) => {
      console.error('remote_getListMapData', error);
    })
  }

  const handleStartProceed = () => {
    remote_mapData_update(uploadResultData.data.id, {
      name: name,
      description: '',
      processInputMetadata: JSON.stringify({
        srs: projection?.proj,
      }),
      processStatus: MapData_ProcessStatus.SUBMITED,
    }).then((response) => {
      console.log('SUCCESS: when remote_mapData_update', response);
      setIsOpen(false);
      reloadMapDataList();

      // reset this component
      setTimeout(() => {
        setComponentKey(componentKey + 1);
        setName('');
        setProjection(undefined);
        setEncoding('');
        setGdalInfo(undefined);
        setOgrInfo(undefined);
      }, 1000);
    }).catch((e) => {
      console.log('ERROR: when remote_mapData_update', e);
    });
  }

  return <Modal
    title='Tải lên dữ liệu'
    open={isOpen}
    onCancel={() => setIsOpen(false)}
    footer={null}
    key={componentKey}
  >
    <UI_Wrapper>
      <Dragger style={{}}
        customRequest={handleUpload}
        multiple={true}
      >
        <p className="ant-upload-drag-icon">
          <InboxOutlined />
        </p>
        <p className="ant-upload-text">Vui lòng chọn hoặc kéo thả tệp vào đây để tải lên</p>
        <p className="ant-upload-hint">
          Đối với các định dạng tệp có các tệp đính kèm (phụ thuộc) như Shapefile, Tab Mapinfo,... bạn cần nén ZIP,
          nếu không thì cần tải lên tất cả các tệp phụ thuộc đó. VD: Shapefile có .shp, .shx, .dbf,...
        </p>
      </Dragger>

      {(gdalInfo || ogrInfo) && (
        <>

          <Input placeholder='Tên hiển thị' style={{ marginBottom: 8, marginTop: 16 }} value={name} onChange={(e) => {
            setName(e.target.value);
          }} />
          <Select
            placeholder='Hệ chiếu' style={{ width: '100%', marginBottom: 8 }}
            showSearch
            onChange={(value) => {
              const projection = mapProjectionList.find((projection) => projection.name === value);
              if (projection) {
                setProjection(projection);
              }
            }}
            value={projection?.name}
          >
            {mapProjectionList.map((projection) => (
              <Select.Option value={projection.name} key={projection.id}>{projection.name}</Select.Option>
            ))}
          </Select>
          <Select placeholder='Bảng mã' style={{ width: '100%', marginBottom: 8 }} />
        </>
      )}

      {ogrInfo && (
        <UI_Data_Info_Wrapper>
          <UI_Data_Info_Title>Thông tin dữ liệu</UI_Data_Info_Title>
          <UI_Content_Row>
            <UI_Content_Row_Label>Tên lớp dữ liệu:</UI_Content_Row_Label>
            <UI_Content_Row_Value>
              {ogrInfo.layers.map((layer: any) => layer.name).join(', ')}
            </UI_Content_Row_Value>
          </UI_Content_Row>
          <UI_Content_Row>
            <UI_Content_Row_Label>Số trường thuộc tính:</UI_Content_Row_Label>
            <UI_Content_Row_Value>
              {ogrInfo.layers.reduce((acc: number, layer: any) => acc + layer.fields.length, 0)}
            </UI_Content_Row_Value>
          </UI_Content_Row>
          <UI_Content_Row>
            <UI_Content_Row_Label>{t('n_records')}:</UI_Content_Row_Label>
            <UI_Content_Row_Value>
              {ogrInfo.layers.reduce((acc: number, layer: any) => acc + layer.featureCount, 0)}
            </UI_Content_Row_Value>
          </UI_Content_Row>
          <UI_Content_Row>
            <UI_Content_Row_Label>Đổ màu theo LDLR:</UI_Content_Row_Label>
            <UI_Content_Row_Value>
              <Switch />
            </UI_Content_Row_Value>
          </UI_Content_Row>
          <UI_Content_Row>
            <UI_Content_Row_Label>Đổ màu theo hiện trạng SD đất:</UI_Content_Row_Label>
            <UI_Content_Row_Value>
              <Switch />
            </UI_Content_Row_Value>
          </UI_Content_Row>
        </UI_Data_Info_Wrapper>
      )}


      <Button
        disabled={!gdalInfo && !ogrInfo}
        type='primary' style={{ width: '100%', marginTop: 16 }} onClick={() => {
          handleStartProceed();
        }}>Tải lên</Button>
    </UI_Wrapper>
  </Modal>
}

export default memo(UploadMapDataPanel);

const UI_Wrapper = styled.div`
`;

const UI_Data_Info_Wrapper = styled.div`
    margin-top: 16px;
    margin-bottom: 16px;
`;

const UI_Data_Info_Title = styled.div`
    font-size: 16px;
    font-weight: 600;
    margin-bottom: 8px;
    text-align: center;
`;

const UI_Content_Row = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    margin-bottom: 8px;
`
const UI_Content_Row_Label = styled.div`
    font-size: 14px;
    margin-right: 8px;
    color: #666666;
    flex: 1;
`
const UI_Content_Row_Value = styled.div`
    flex: 1;
    font-size: 14px;
    font-weight: 600;
    text-align: right;
`